import React, { useState } from "react";
import type { ConversationDailyRow } from "../../types";

interface ConversationDailyTrendChartProps {
  data: ConversationDailyRow[];
  dateRange: { start: string; end: string };
  timezone?: string;
}

export const ConversationDailyTrendChart: React.FC<ConversationDailyTrendChartProps> = ({
  data,
  dateRange,
  timezone = "America/Santiago",
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // Generar todos los días del rango para que no queden huecos
  const allDaysMap = new Map<string, ConversationDailyRow>();
  for (const row of data) {
    allDaysMap.set(row.date, row);
  }

  // Generar lista continua de fechas entre start y end
  const startDate = new Date(dateRange.start + "T00:00:00");
  const endDate = new Date(dateRange.end + "T00:00:00");
  const fullSeries: Array<{ date: string; started: number; handedOff: number }> = [];

  const curr = new Date(startDate);
  while (curr <= endDate && fullSeries.length < 400) {
    const dStr = curr.toISOString().split("T")[0];
    const match = allDaysMap.get(dStr);
    fullSeries.push({
      date: dStr,
      started: match?.started_count ?? 0,
      handedOff: match?.handed_off_count ?? 0,
    });
    curr.setDate(curr.getDate() + 1);
  }

  const maxVal = Math.max(1, ...fullSeries.map((d) => d.started));

  const chartHeight = 220;
  const paddingLeft = 40;
  const paddingRight = 20;
  const paddingTop = 20;
  const paddingBottom = 40;
  const availableWidth = 800; // viewBox scale
  const plotWidth = availableWidth - paddingLeft - paddingRight;
  const plotHeight = chartHeight - paddingTop - paddingBottom;

  const colWidth = fullSeries.length > 0 ? plotWidth / fullSeries.length : 10;
  const barWidth = Math.max(2, Math.min(24, colWidth * 0.7));

  const activeRow = hoveredIndex !== null ? fullSeries[hoveredIndex] : null;

  return (
    <div
      style={{
        background: "var(--bg-surface, #111827)",
        border: "1px solid var(--border-subtle, rgba(255,255,255,0.08))",
        borderRadius: "8px",
        padding: "20px",
        marginBottom: "24px",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px", flexWrap: "wrap", gap: "8px" }}>
        <div>
          <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 600, color: "var(--text-primary, #f9fafb)" }}>
            Por día
          </h3>
          <p style={{ margin: "4px 0 0", fontSize: "12px", color: "var(--text-muted, #9ca3af)" }}>
            Evolución diaria de conversaciones iniciadas y contactos que pasaron por derivación · Zona horaria: {timezone}
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "14px", fontSize: "12px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span style={{ width: "12px", height: "12px", borderRadius: "2px", background: "rgba(59, 130, 246, 0.4)", display: "inline-block" }} />
            <span style={{ color: "var(--text-muted)" }}>Claro: Iniciadas</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span style={{ width: "12px", height: "12px", borderRadius: "2px", background: "#1d5f96", display: "inline-block" }} />
            <span style={{ color: "var(--text-muted)" }}>Oscuro: Pasaron por derivación</span>
          </div>
          <span style={{ color: "var(--text-muted)", background: "rgba(255,255,255,0.06)", padding: "2px 6px", borderRadius: "4px" }}>
            Máximo: {maxVal}
          </span>
        </div>
      </div>

      {activeRow && (
        <div
          style={{
            padding: "8px 12px",
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: "6px",
            fontSize: "12px",
            marginBottom: "12px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <strong style={{ color: "var(--text-primary)" }}>{activeRow.date}</strong>
          <span>
            Iniciadas: <strong style={{ color: "#60a5fa" }}>{activeRow.started}</strong>
          </span>
          <span>
            Pasaron por derivación: <strong style={{ color: "#38bdf8" }}>{activeRow.handedOff}</strong>
          </span>
        </div>
      )}

      {/* SVG Responsive de barras superpuestas */}
      <div style={{ width: "100%", overflowX: "auto" }}>
        <svg
          viewBox={`0 0 ${availableWidth} ${chartHeight}`}
          style={{ width: "100%", height: "auto", display: "block" }}
          role="img"
          aria-label="Serie temporal de conversaciones por día"
          onMouseLeave={() => setHoveredIndex(null)}
        >
          {/* Líneas de Guía Horizontales */}
          <line
            x1={paddingLeft}
            y1={paddingTop}
            x2={availableWidth - paddingRight}
            y2={paddingTop}
            stroke="rgba(255,255,255,0.08)"
            strokeDasharray="4 4"
          />
          <text x={paddingLeft - 8} y={paddingTop + 4} fill="var(--text-muted, #9ca3af)" fontSize="10" textAnchor="end">
            {maxVal}
          </text>

          <line
            x1={paddingLeft}
            y1={paddingTop + plotHeight / 2}
            x2={availableWidth - paddingRight}
            y2={paddingTop + plotHeight / 2}
            stroke="rgba(255,255,255,0.05)"
            strokeDasharray="4 4"
          />
          <text x={paddingLeft - 8} y={paddingTop + plotHeight / 2 + 4} fill="var(--text-muted, #9ca3af)" fontSize="10" textAnchor="end">
            {Math.round(maxVal / 2)}
          </text>

          <line
            x1={paddingLeft}
            y1={chartHeight - paddingBottom}
            x2={availableWidth - paddingRight}
            y2={chartHeight - paddingBottom}
            stroke="rgba(255,255,255,0.15)"
          />
          <text x={paddingLeft - 8} y={chartHeight - paddingBottom + 4} fill="var(--text-muted, #9ca3af)" fontSize="10" textAnchor="end">
            0
          </text>

          {/* Barras por Día */}
          {fullSeries.map((row, idx) => {
            const xCenter = paddingLeft + (idx + 0.5) * colWidth;
            const x = xCenter - barWidth / 2;

            const startedH = maxVal > 0 ? (row.started / maxVal) * plotHeight : 0;
            const handedOffH = maxVal > 0 ? (row.handedOff / maxVal) * plotHeight : 0;

            const yStarted = chartHeight - paddingBottom - startedH;
            const yHandedOff = chartHeight - paddingBottom - handedOffH;

            const isHovered = hoveredIndex === idx;

            return (
              <g
                key={row.date}
                onMouseEnter={() => setHoveredIndex(idx)}
                style={{ cursor: "pointer" }}
              >
                {/* Hitbox amplio para hover */}
                <rect
                  x={xCenter - colWidth / 2}
                  y={paddingTop}
                  width={colWidth}
                  height={plotHeight}
                  fill="transparent"
                />

                {/* Barra clara de fondo: Iniciadas */}
                {row.started > 0 && (
                  <rect
                    x={x}
                    y={yStarted}
                    width={barWidth}
                    height={startedH}
                    fill={isHovered ? "rgba(59, 130, 246, 0.6)" : "rgba(59, 130, 246, 0.35)"}
                    rx={2}
                  />
                )}

                {/* Barra oscura superpuesta: Pasaron por derivación */}
                {row.handedOff > 0 && (
                  <rect
                    x={x}
                    y={yHandedOff}
                    width={barWidth}
                    height={handedOffH}
                    fill={isHovered ? "#2563eb" : "#1d5f96"}
                    rx={2}
                  />
                )}
              </g>
            );
          })}

          {/* Etiquetas Eje X: Primera y Última */}
          {fullSeries.length > 0 && (
            <>
              <text
                x={paddingLeft}
                y={chartHeight - 12}
                fill="var(--text-muted, #9ca3af)"
                fontSize="11"
                textAnchor="start"
              >
                {fullSeries[0].date.slice(5).replace("-", "/")}
              </text>
              <text
                x={availableWidth - paddingRight}
                y={chartHeight - 12}
                fill="var(--text-muted, #9ca3af)"
                fontSize="11"
                textAnchor="end"
              >
                {fullSeries[fullSeries.length - 1].date}
              </text>
            </>
          )}
        </svg>
      </div>
    </div>
  );
};
